// oxlint-disable default-case
import { VIEW_PREFIX } from './constants';

export type AccountResource = {
  type: 'acct';
};

export type IpAllowlistResource = {
  type: 'ip-allowlist';
};

export type ApplicationResource = {
  type: 'application';
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type ApplicationVersionResource = {
  type: 'version';
  application: ApplicationResource;
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type ContextKindResource = {
  type: 'context-kind';
  project: ProjectResource;
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type ReleasePipelineResource = {
  type: 'release-pipeline';
  project: ProjectResource;
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type ReleasePolicyResource = {
  type: 'release-policy';
  project: ProjectResource;
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type CodeReferencesRepositoryResource = {
  type: 'code-reference-repository';
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type DestinationResource = {
  type: 'destination';
  environment: EnvironmentResource;
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type DomainVerificationResource = {
  type: 'domain-verification';
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type EnvironmentResource = {
  type: 'env';
  project: ProjectResource;
  name: string;
  tags: string[];
  critical?: boolean;
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type PropertySelectors = {
  critical?: boolean;
  affectsSdkPayload?: boolean;
};

export type ExperimentResource = {
  type: 'experiment';
  environment: EnvironmentResource;
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type MultiArmedBanditResource = {
  type: 'multi-armed-bandit';
  environment: EnvironmentResource;
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type FlagResource = {
  // feature is a deprecated type. Equates to flag.
  type: 'flag' | 'feature';
  environment: EnvironmentResource;
  name: string;
  tags: string[];
  views: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
  viewsRoleAttribute?: string;
};

export type IntegrationResource = {
  type: 'integration';
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type LayerResource = {
  type: 'layer';
  project: ProjectResource;
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type HoldoutResource = {
  type: 'holdout';
  environment: EnvironmentResource;
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type MemberResource = {
  type: 'member';
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type MetricResource = {
  type: 'metric';
  project: ProjectResource;
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type MetricGroup = {
  type: 'metric-group';
  project: ProjectResource;
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type MetricDataSourceResource = {
  type: 'metric-data-source';
  environment: EnvironmentResource;
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type AssignmentDataSourceResource = {
  type: 'assignment-data-source';
  environment: EnvironmentResource;
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type GoalResource = {
  type: 'goal';
  project: ProjectResource;
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type ProjectResource = {
  type: 'proj';
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type RelayProxyConfigResource = {
  type: 'relay-proxy-config';
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type RoleResource = {
  type: 'role';
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type SegmentResource = {
  type: 'segment';
  environment: EnvironmentResource;
  name: string;
  tags: string[];
  views: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
  viewsRoleAttribute?: string;
};

export type ServiceTokenResource = {
  type: 'service-token';
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type PendingRequestResource = {
  type: 'pending-request';
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type TeamResource = {
  type: 'team';
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type TemplateResource = {
  type: 'template';
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type TokenResource = {
  type: 'token';
  member: MemberResource;
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type UserResource = {
  type: 'user';
  environment: EnvironmentResource;
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type PayloadFilterResource = {
  type: 'payload-filter';
  project: ProjectResource;
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type WebhookResource = {
  type: 'webhook';
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type AiConfigResource = {
  type: 'aiconfig';
  environment: EnvironmentResource;
  name: string;
  tags: string[];
  views: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
  viewsRoleAttribute?: string;
};

export type AiModelConfigResource = {
  project: ProjectResource;
  type: 'ai-model-config';
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type AiDatasetResource = {
  project: ProjectResource;
  type: 'ai-dataset';
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type AiToolResource = {
  project: ProjectResource;
  type: 'ai-tool';
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type PromptSnippetResource = {
  project: ProjectResource;
  type: 'prompt-snippet';
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type AiEvaluationResource = {
  project: ProjectResource;
  type: 'ai-evaluation';
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type ViewResource = {
  type: 'view';
  project: ProjectResource;
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
  /**
   * The view's `_affectsSdkPayload` property. Serialized as the
   * `{affectsSdkPayload:<bool>}` property selector so role statements scoped
   * by it (payload-filtering custom roles) match the checked resource.
   */
  affectsSdkPayload?: boolean;
};

export type ProductAnalyticsDashboardResource = {
  type: 'product-analytics-dashboard';
  environment: EnvironmentResource;
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

// Observability resources
export type SessionResource = {
  type: 'session';
  project: ProjectResource;
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type ErrorResource = {
  type: 'error';
  project: ProjectResource;
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type LogResource = {
  type: 'log';
  project: ProjectResource;
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type TraceResource = {
  type: 'trace';
  project: ProjectResource;
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type VegaResource = {
  type: 'vega';
  project: ProjectResource;
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type AlertResource = {
  type: 'alert';
  project: ProjectResource;
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type ObservabilityDashboardResource = {
  type: 'observability-dashboard';
  project: ProjectResource;
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type ObservabilitySettingsResource = {
  type: 'observability-settings';
  project: ProjectResource;
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type NotificationSubscriptionResource = {
  type: 'notification-subscription';
  environment: EnvironmentResource;
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type SdkKeyResource = {
  type: 'sdk-key';
  environment: EnvironmentResource;
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type EnvironmentReleasePlanResource = {
  type: 'environment-release-plan';
  environment: EnvironmentResource;
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type AgentGraphResource = {
  type: 'agent-graph';
  project: ProjectResource;
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type AgentOptimizationResource = {
  type: 'agent-optimization';
  project: ProjectResource;
  name: string;
  tags: string[];
  roleAttribute?: string;
  tagsRoleAttribute?: string;
};

export type ResourceSpecifier =
  | AiConfigResource
  | AiDatasetResource
  | AiModelConfigResource
  | AiToolResource
  | PromptSnippetResource
  | AiEvaluationResource
  | AccountResource
  | CodeReferencesRepositoryResource
  | DestinationResource
  | EnvironmentResource
  | ExperimentResource
  | FlagResource
  | GoalResource
  | HoldoutResource
  | IntegrationResource
  | LayerResource
  | MemberResource
  | MetricResource
  | MetricGroup
  | MetricDataSourceResource
  | AssignmentDataSourceResource
  | MultiArmedBanditResource
  | PayloadFilterResource
  | PendingRequestResource
  | ProjectResource
  | RelayProxyConfigResource
  | RoleResource
  | SegmentResource
  | ServiceTokenResource
  | TeamResource
  | TemplateResource
  | TokenResource
  | UserResource
  | WebhookResource
  | ApplicationResource
  | ApplicationVersionResource
  | ReleasePipelineResource
  | ReleasePolicyResource
  | ContextKindResource
  | DomainVerificationResource
  | ViewResource
  | ProductAnalyticsDashboardResource
  | SessionResource
  | ErrorResource
  | LogResource
  | TraceResource
  | VegaResource
  | AlertResource
  | ObservabilityDashboardResource
  | ObservabilitySettingsResource
  | NotificationSubscriptionResource
  | SdkKeyResource
  | EnvironmentReleasePlanResource
  | AgentGraphResource
  | AgentOptimizationResource
  | IpAllowlistResource;

export type ResourceType = ResourceSpecifier['type'];

export function isResourceType(v: unknown): v is ResourceType {
  if (typeof v !== 'string') {
    return false;
  }

  switch (v) {
    case 'acct':
    case 'application':
    case 'version':
    case 'code-reference-repository':
    case 'destination':
    case 'domain-verification':
    case 'env':
    case 'experiment':
    case 'feature':
    case 'flag':
    case 'goal':
    case 'holdout':
    case 'integration':
    case 'layer':
    case 'member':
    case 'metric':
    case 'metric-group':
    case 'metric-data-source':
    case 'assignment-data-source':
    case 'multi-armed-bandit':
    case 'payload-filter':
    case 'pending-request':
    case 'proj':
    case 'relay-proxy-config':
    case 'release-pipeline':
    case 'release-policy':
    case 'role':
    case 'segment':
    case 'service-token':
    case 'team':
    case 'template':
    case 'token':
    case 'user':
    case 'webhook':
    case 'context-kind':
    case 'aiconfig':
    case 'ai-dataset':
    case 'ai-model-config':
    case 'ai-tool':
    case 'prompt-snippet':
    case 'ai-evaluation':
    case 'view':
    case 'product-analytics-dashboard':
    case 'session':
    case 'error':
    case 'log':
    case 'trace':
    case 'vega':
    case 'alert':
    case 'observability-dashboard':
    case 'observability-settings':
    case 'notification-subscription':
    case 'sdk-key':
    case 'environment-release-plan':
    case 'agent-graph':
    case 'agent-optimization':
    case 'ip-allowlist':
      return true;
  }

  return false;
}

export function account(): AccountResource {
  return { type: 'acct' };
}

export function ipAllowlist(): IpAllowlistResource {
  return { type: 'ip-allowlist' };
}

export function application(
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): ApplicationResource {
  return { type: 'application', name, tags: tags ?? [], roleAttribute, tagsRoleAttribute };
}

export function applicationVersion(
  _application: ApplicationResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): ApplicationVersionResource {
  return { type: 'version', application: _application, name, tags: tags ?? [], roleAttribute, tagsRoleAttribute };
}

export function domainVerification(
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): DomainVerificationResource {
  return { type: 'domain-verification', name, tags: tags ?? [], roleAttribute, tagsRoleAttribute };
}

export function releasePipeline(
  _project: ProjectResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): ReleasePipelineResource {
  return { type: 'release-pipeline', project: _project, name, tags: tags ?? [], roleAttribute, tagsRoleAttribute };
}

export function releasePolicy(
  _project: ProjectResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): ReleasePolicyResource {
  return { type: 'release-policy', project: _project, name, tags: tags ?? [], roleAttribute, tagsRoleAttribute };
}

export function repository(
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): CodeReferencesRepositoryResource {
  return { type: 'code-reference-repository', name, tags: tags ?? [], roleAttribute, tagsRoleAttribute };
}

export function destination(
  _environment: EnvironmentResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): DestinationResource {
  return {
    type: 'destination',
    environment: _environment,
    name,
    tags: tags ?? [],
    roleAttribute,
    tagsRoleAttribute,
  };
}

export function environment(
  _project: ProjectResource,
  name: string,
  tags: string[] = [],
  critical?: boolean,
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): EnvironmentResource {
  return { type: 'env', project: _project, name, tags: tags ?? [], critical, roleAttribute, tagsRoleAttribute };
}

export function experiment(
  _environment: EnvironmentResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): ExperimentResource {
  return { type: 'experiment', environment: _environment, name, tags: tags ?? [], roleAttribute, tagsRoleAttribute };
}

export function multiArmedBandit(
  _environment: EnvironmentResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): MultiArmedBanditResource {
  return {
    type: 'multi-armed-bandit',
    environment: _environment,
    name,
    tags: tags ?? [],
    roleAttribute,
    tagsRoleAttribute,
  };
}

export function flag(
  _environment: EnvironmentResource,
  name: string,
  tags: string[] = [],
  views: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
  viewsRoleAttribute?: string,
): FlagResource {
  return {
    type: 'flag',
    environment: _environment,
    name,
    tags: tags ?? [],
    views: views ?? [],
    roleAttribute,
    tagsRoleAttribute,
    viewsRoleAttribute,
  };
}

export function goal(
  _project: ProjectResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): GoalResource {
  return { type: 'goal', project: _project, name, tags: tags ?? [], roleAttribute, tagsRoleAttribute };
}

export function holdout(
  _environment: EnvironmentResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): HoldoutResource {
  return { type: 'holdout', environment: _environment, name, tags: tags ?? [], roleAttribute, tagsRoleAttribute };
}

export function integration(
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): IntegrationResource {
  return { type: 'integration', name, tags: tags ?? [], roleAttribute, tagsRoleAttribute };
}

export function layer(
  _project: ProjectResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): LayerResource {
  return { type: 'layer', project: _project, name, tags: tags ?? [], roleAttribute, tagsRoleAttribute };
}

export function member(
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): MemberResource {
  return { type: 'member', name, tags: tags ?? [], roleAttribute, tagsRoleAttribute };
}

export function metric(
  _project: ProjectResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): MetricResource {
  return { type: 'metric', project: _project, name, tags: tags ?? [], roleAttribute, tagsRoleAttribute };
}

export function metricGroup(
  _project: ProjectResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): MetricGroup {
  return { type: 'metric-group', project: _project, name, tags: tags ?? [], roleAttribute, tagsRoleAttribute };
}

export function metricDataSource(
  _environment: EnvironmentResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): MetricDataSourceResource {
  return {
    type: 'metric-data-source',
    environment: _environment,
    name,
    tags: tags ?? [],
    roleAttribute,
    tagsRoleAttribute,
  };
}

export function assignmentDataSource(
  _environment: EnvironmentResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): AssignmentDataSourceResource {
  return {
    type: 'assignment-data-source',
    environment: _environment,
    name,
    tags: tags ?? [],
    roleAttribute,
    tagsRoleAttribute,
  };
}

export function payloadFilter(
  _project: ProjectResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): PayloadFilterResource {
  return { type: 'payload-filter', project: _project, name, tags: tags ?? [], roleAttribute, tagsRoleAttribute };
}

export function project(
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): ProjectResource {
  return { type: 'proj', name, tags: tags ?? [], roleAttribute, tagsRoleAttribute };
}

export function pendingRequest(
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): PendingRequestResource {
  return { type: 'pending-request', name, tags: tags ?? [], roleAttribute, tagsRoleAttribute };
}

export function relayProxyConfig(
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): RelayProxyConfigResource {
  return { type: 'relay-proxy-config', name, tags: tags ?? [], roleAttribute, tagsRoleAttribute };
}

export function role(
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): RoleResource {
  return { type: 'role', name, tags: tags ?? [], roleAttribute, tagsRoleAttribute };
}

export function segment(
  _environment: EnvironmentResource,
  name: string,
  tags: string[] = [],
  views: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
  viewsRoleAttribute?: string,
): SegmentResource {
  return {
    type: 'segment',
    environment: _environment,
    name,
    tags: tags ?? [],
    views: views ?? [],
    roleAttribute,
    tagsRoleAttribute,
    viewsRoleAttribute,
  };
}

export function serviceToken(
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): ServiceTokenResource {
  return { type: 'service-token', name, tags: tags ?? [], roleAttribute, tagsRoleAttribute };
}

export function team(
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): TeamResource {
  return { type: 'team', name, tags: tags ?? [], roleAttribute, tagsRoleAttribute };
}

export function template(
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): TemplateResource {
  return { type: 'template', name, tags: tags ?? [], roleAttribute, tagsRoleAttribute };
}

export function contextKind(
  _project: ProjectResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): ContextKindResource {
  return { type: 'context-kind', project: _project, name, tags: tags ?? [], roleAttribute, tagsRoleAttribute };
}

export function token(
  _member: MemberResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): TokenResource {
  return { type: 'token', member: _member, name, tags: tags ?? [], roleAttribute, tagsRoleAttribute };
}

export function user(
  _environment: EnvironmentResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): UserResource {
  return { type: 'user', environment: _environment, name, tags: tags ?? [], roleAttribute, tagsRoleAttribute };
}

export function webhook(
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): WebhookResource {
  return { type: 'webhook', name, tags: tags ?? [], roleAttribute, tagsRoleAttribute };
}

export function aiConfig(
  _environment: EnvironmentResource,
  name: string,
  tags: string[] = [],
  views: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
  viewsRoleAttribute?: string,
): AiConfigResource {
  return {
    type: 'aiconfig',
    environment: _environment,
    name,
    tags: tags ?? [],
    views: views ?? [],
    roleAttribute,
    tagsRoleAttribute,
    viewsRoleAttribute,
  };
}

export function aiModelConfig(
  _project: ProjectResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): AiModelConfigResource {
  return { type: 'ai-model-config', project: _project, name, tags: tags ?? [], roleAttribute, tagsRoleAttribute };
}

export function aiDataset(
  _project: ProjectResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): AiDatasetResource {
  return { type: 'ai-dataset', project: _project, name, tags, roleAttribute, tagsRoleAttribute };
}

export function aiTool(
  _project: ProjectResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): AiToolResource {
  return { type: 'ai-tool', project: _project, name, tags: tags ?? [], roleAttribute, tagsRoleAttribute };
}

export function promptSnippet(
  _project: ProjectResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): PromptSnippetResource {
  return { type: 'prompt-snippet', project: _project, name, tags: tags ?? [], roleAttribute, tagsRoleAttribute };
}

export function aiEvaluation(
  _project: ProjectResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): AiEvaluationResource {
  return { type: 'ai-evaluation', project: _project, name, tags: tags ?? [], roleAttribute, tagsRoleAttribute };
}

export function view(
  _project: ProjectResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
  affectsSdkPayload?: boolean,
): ViewResource {
  return {
    type: 'view',
    project: _project,
    name,
    tags: tags ?? [],
    roleAttribute,
    tagsRoleAttribute,
    affectsSdkPayload,
  };
}

export function productAnalyticsDashboard(
  _environment: EnvironmentResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): ProductAnalyticsDashboardResource {
  return {
    type: 'product-analytics-dashboard',
    environment: _environment,
    name,
    tags: tags ?? [],
    roleAttribute,
    tagsRoleAttribute,
  };
}

// Observability resource helper functions
export function session(
  _project: ProjectResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): SessionResource {
  return {
    type: 'session',
    project: _project,
    name,
    tags: tags ?? [],
    roleAttribute,
    tagsRoleAttribute,
  };
}

export function error(
  _project: ProjectResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): ErrorResource {
  return {
    type: 'error',
    project: _project,
    name,
    tags: tags ?? [],
    roleAttribute,
    tagsRoleAttribute,
  };
}

export function log(
  _project: ProjectResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): LogResource {
  return {
    type: 'log',
    project: _project,
    name,
    tags: tags ?? [],
    roleAttribute,
    tagsRoleAttribute,
  };
}

export function trace(
  _project: ProjectResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): TraceResource {
  return {
    type: 'trace',
    project: _project,
    name,
    tags: tags ?? [],
    roleAttribute,
    tagsRoleAttribute,
  };
}

export function vega(
  _project: ProjectResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): VegaResource {
  return {
    type: 'vega',
    project: _project,
    name,
    tags: tags ?? [],
    roleAttribute,
    tagsRoleAttribute,
  };
}

export function alert(
  _project: ProjectResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): AlertResource {
  return {
    type: 'alert',
    project: _project,
    name,
    tags: tags ?? [],
    roleAttribute,
    tagsRoleAttribute,
  };
}

export function observabilityDashboard(
  _project: ProjectResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): ObservabilityDashboardResource {
  return {
    type: 'observability-dashboard',
    project: _project,
    name,
    tags: tags ?? [],
    roleAttribute,
    tagsRoleAttribute,
  };
}

export function observabilitySettings(
  _project: ProjectResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): ObservabilitySettingsResource {
  return {
    type: 'observability-settings',
    project: _project,
    name,
    tags: tags ?? [],
    roleAttribute,
    tagsRoleAttribute,
  };
}

export function sdkKey(
  _environment: EnvironmentResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): SdkKeyResource {
  return {
    type: 'sdk-key',
    environment: _environment,
    name,
    tags: tags ?? [],
    roleAttribute,
    tagsRoleAttribute,
  };
}

export function environmentReleasePlan(
  _environment: EnvironmentResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): EnvironmentReleasePlanResource {
  return {
    type: 'environment-release-plan',
    environment: _environment,
    name,
    tags: tags ?? [],
    roleAttribute,
    tagsRoleAttribute,
  };
}

export function notificationSubscription(
  _environment: EnvironmentResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): NotificationSubscriptionResource {
  return {
    type: 'notification-subscription',
    environment: _environment,
    name,
    tags: tags ?? [],
    roleAttribute,
    tagsRoleAttribute,
  };
}

export function agentGraph(
  _project: ProjectResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): AgentGraphResource {
  return {
    type: 'agent-graph',
    project: _project,
    name,
    tags,
    roleAttribute,
    tagsRoleAttribute,
  };
}

export function agentOptimization(
  _project: ProjectResource,
  name: string,
  tags: string[] = [],
  roleAttribute?: string,
  tagsRoleAttribute?: string,
): AgentOptimizationResource {
  return {
    type: 'agent-optimization',
    project: _project,
    name,
    tags,
    roleAttribute,
    tagsRoleAttribute,
  };
}

export function toReadableString(node: ResourceSpecifier): string {
  const getNameAndTag = (singular: string, plural: string, n: ResourceSpecifier) => {
    let result = '';
    if (n.type === 'acct' || n.type === 'ip-allowlist') {
      return result;
    }
    if (n.type === 'env' && n.critical !== undefined && n.critical) {
      result = n.name !== '*' ? `**Critical ${singular}**: ${n.name}` : `**All Critical ${plural}**`;
    } else if (n.type === 'env' && n.critical !== undefined && !n.critical) {
      result = n.name !== '*' ? `**Non-critical ${singular}**: ${n.name}` : `**All Non-critical ${plural}**`;
    } else {
      result = n.name !== '*' ? `**${singular}**: ${n.name}` : `**All ${plural}**`;
    }

    if ((n.tags && n.tags.length > 0) || !!n.tagsRoleAttribute) {
      // result += ` with **Tags**: [${n.tags.join(', ')}]`;
      result += ` with **Tags**:`;
      result += n.tagsRoleAttribute ? ` \${roleAttribute/${n.tagsRoleAttribute}}` : '';
      result += !!n.tagsRoleAttribute && n.tags && n.tags.length > 0 ? ' and' : '';
      result += n.tags && n.tags.length > 0 ? ` [${n.tags.join(', ')}]` : '';
    }

    if ((n.type === 'flag' || n.type === 'feature') && ((n.views && n.views.length > 0) || !!n.viewsRoleAttribute)) {
      result += ` in **Views**:`;
      result += n.viewsRoleAttribute ? ` \${roleAttribute/${n.viewsRoleAttribute}}` : '';
      result += !!n.viewsRoleAttribute && n.views && n.views.length > 0 ? ' and' : '';
      result += n.views && n.views.length > 0 ? ` [${n.views.join(', ')}]` : '';
    }

    return result;
  };

  let result = '';
  switch (node.type) {
    case 'acct':
      result = '**Account**';
      break;
    case 'ip-allowlist':
      result = '**IP allowlist**';
      break;
    case 'application':
      result = getNameAndTag('Application', 'Applications', node);
      break;
    case 'code-reference-repository':
      result = getNameAndTag('Code Reference Repository', 'code reference repositories', node);
      break;
    case 'destination':
      result = [toReadableString(node.environment), getNameAndTag('Destination', 'destinations', node)].join(', ');
      break;
    case 'domain-verification':
      result = getNameAndTag('Domain Verification', 'Domain Verifications', node);
      break;
    case 'env':
      result = [toReadableString(node.project), getNameAndTag('Environment', 'Environments', node)].join(', ');
      break;
    case 'experiment':
      result = [toReadableString(node.environment), getNameAndTag('Experiment', 'Experiments', node)].join(', ');
      break;
    case 'flag':
      result = [toReadableString(node.environment), getNameAndTag('Flag', 'Flags', node)].join(', ');
      break;
    case 'goal':
      result = [toReadableString(node.project), getNameAndTag('Goal', 'Goals', node)].join(', ');
      break;
    case 'holdout':
      result = [toReadableString(node.environment), getNameAndTag('Holdout', 'Holdouts', node)].join(', ');
      break;
    case 'integration':
      result = getNameAndTag('Integration', 'Integrations', node);
      break;
    case 'layer':
      result = [toReadableString(node.project), getNameAndTag('Layer', 'Layers', node)].join(', ');
      break;
    case 'member':
      result = getNameAndTag('Member', 'Members', node);
      break;
    case 'metric':
      result = [toReadableString(node.project), getNameAndTag('Metric', 'Metrics', node)].join(', ');
      break;
    case 'metric-group':
      result = [toReadableString(node.project), getNameAndTag('Metric Group', 'Metric Groups', node)].join(', ');
      break;
    case 'metric-data-source':
      result = [
        toReadableString(node.environment),
        getNameAndTag('Metric Data Source', 'Metric Data Sources', node),
      ].join(', ');
      break;
    case 'assignment-data-source':
      result = [
        toReadableString(node.environment),
        getNameAndTag('Assignment Data Source', 'Assignment Data Sources', node),
      ].join(', ');
      break;
    case 'multi-armed-bandit':
      result = [
        toReadableString(node.environment),
        getNameAndTag('Mutli-armed Bandit', 'Multi-armed Bandit', node),
      ].join(', ');
      break;
    case 'context-kind':
      result = [toReadableString(node.project), getNameAndTag('Context Kind', 'Context Kinds', node)].join(', ');
      break;
    case 'pending-request':
      result = getNameAndTag('Pending Request', 'Pending Requests', node);
      break;
    case 'payload-filter':
      result = [toReadableString(node.project), getNameAndTag('Payload Filter', 'Payload Filters', node)].join(', ');
      break;
    case 'proj':
      result = getNameAndTag('Project', 'Projects', node);
      break;
    case 'relay-proxy-config':
      result = getNameAndTag('Relay Proxy Config', 'Relay Proxy Configs', node);
      break;
    case 'release-pipeline':
      result = [toReadableString(node.project), getNameAndTag('Release Pipeline', 'Release Pipelines', node)].join(
        ', ',
      );
      break;
    case 'release-policy':
      result = [toReadableString(node.project), getNameAndTag('Release Policy', 'Release Policies', node)].join(', ');
      break;
    case 'role':
      result = getNameAndTag('Role', 'Roles', node);
      break;
    case 'segment':
      result = [toReadableString(node.environment), getNameAndTag('Segment', 'Segments', node)].join(', ');
      break;
    case 'notification-subscription':
      result = [
        toReadableString(node.environment),
        getNameAndTag('Notification Subscription', 'Notification Subscriptions', node),
      ].join(', ');
      break;
    case 'sdk-key':
      result = [toReadableString(node.environment), getNameAndTag('SDK Key', 'SDK Keys', node)].join(', ');
      break;
    case 'environment-release-plan':
      result = [
        toReadableString(node.environment),
        getNameAndTag('Environment Release Plan', 'Environment Release Plans', node),
      ].join(', ');
      break;
    case 'service-token':
      result = getNameAndTag('Service Token', 'Service Tokens', node);
      break;
    case 'team':
      result = getNameAndTag('Team', 'Teams', node);
      break;
    case 'template':
      result = getNameAndTag('Template', 'Templates', node);
      break;
    case 'token':
      result = [getNameAndTag('Member', 'Members', node.member), getNameAndTag('Token', 'Tokens', node)].join(', ');
      break;
    case 'user':
      result = [toReadableString(node.environment), getNameAndTag('User', 'Users', node)].join(', ');
      break;
    case 'webhook':
      result = getNameAndTag('Webhook', 'Webhooks', node);
      break;
    case 'aiconfig':
      result = [toReadableString(node.environment), getNameAndTag('AI config', 'AI configs', node)].join(', ');
      break;
    case 'ai-model-config':
      result = [toReadableString(node.project), getNameAndTag('AI Model Config', 'AI Model Configs', node)].join(', ');
      break;
    case 'ai-dataset':
      result = [toReadableString(node.project), getNameAndTag('Dataset', 'Datasets', node)].join(', ');
      break;
    case 'ai-tool':
      result = [toReadableString(node.project), getNameAndTag('AI Tool', 'AI Tools', node)].join(', ');
      break;
    case 'prompt-snippet':
      result = [toReadableString(node.project), getNameAndTag('Prompt snippet', 'Prompt snippets', node)].join(', ');
      break;
    case 'ai-evaluation':
      result = [toReadableString(node.project), getNameAndTag('AI evaluation', 'AI evaluations', node)].join(', ');
      break;
    case 'view':
      result = [toReadableString(node.project), getNameAndTag('View', 'Views', node)].join(', ');
      break;

    case 'product-analytics-dashboard':
      result = [
        toReadableString(node.environment),
        getNameAndTag('Product analytics Dashboard', 'Product analytics Dashboards', node),
      ].join(', ');
      break;

    // Observability resources
    case 'session':
      result = [toReadableString(node.project), getNameAndTag('Session', 'Sessions', node)].join(', ');
      break;
    case 'error':
      result = [toReadableString(node.project), getNameAndTag('Error', 'Errors', node)].join(', ');
      break;
    case 'log':
      result = [toReadableString(node.project), getNameAndTag('Log', 'Logs', node)].join(', ');
      break;
    case 'trace':
      result = [toReadableString(node.project), getNameAndTag('Trace', 'Traces', node)].join(', ');
      break;
    case 'vega':
      result = [toReadableString(node.project), getNameAndTag('Vega', 'Vegas', node)].join(', ');
      break;
    case 'alert':
      result = [toReadableString(node.project), getNameAndTag('Alert', 'Alerts', node)].join(', ');
      break;
    case 'observability-dashboard':
      result = [
        toReadableString(node.project),
        getNameAndTag('Observability Dashboard', 'Observability Dashboards', node),
      ].join(', ');
      break;
    case 'agent-graph':
      result = [toReadableString(node.project), getNameAndTag('Agent Graph', 'Agent Graphs', node)].join(', ');
      break;
    case 'agent-optimization':
      result = [toReadableString(node.project), getNameAndTag('Agent Optimization', 'Agent Optimizations', node)].join(
        ', ',
      );
      break;
    case 'observability-settings':
      result = [
        toReadableString(node.project),
        getNameAndTag('Observability Settings', 'Observability Settings', node),
      ].join(', ');
      break;
  }
  return result;
}

/**
 * Convert an AST to a string.
 *
 * Note that all (expression) operators (and/or) have equal
 * precedence so we don't both with parentheses when
 * serializing. Should we add operators with higher precedence
 * that will need to change (along with the rest of the parser).
 *
 */
export function toString(node: ResourceSpecifier): string {
  function stringifyTags(tags: string[] | null | undefined, tagsRoleAttribute?: string) {
    // Defensively coerce to an empty array. Even though the type claims `string[]`,
    // upstream callers (e.g. project/environment objects returned by the API) may
    // supply `null`, which would otherwise crash with "Cannot read properties of
    // null (reading 'length')".
    const safeTags = tags ?? [];
    const roleAttributeTag = tagsRoleAttribute ? `\${roleAttribute/${tagsRoleAttribute}}` : undefined;
    let metaTag: string | undefined;
    if (node.type === 'env' && node.critical !== undefined) {
      metaTag = `{critical:${node.critical}}`;
    }
    if (node.type === 'view' && node.affectsSdkPayload !== undefined) {
      metaTag = `{affectsSdkPayload:${node.affectsSdkPayload}}`;
    }
    if (safeTags.length === 0 && metaTag === undefined && !roleAttributeTag) {
      return '';
    }

    const ret: string[] = [];

    if (roleAttributeTag) {
      ret.push(roleAttributeTag);
    }

    const sorted = Array.from(safeTags).sort();
    ret.push(...sorted);

    if (metaTag) {
      ret.push(metaTag);
    }

    return `;${ret.join(',')}`;
  }

  function stringifyViews(
    tags: string[] | null | undefined,
    views: string[] | null | undefined,
    tagsRoleAttribute?: string,
    viewsRoleAttribute?: string,
  ) {
    // Defensively coerce to empty arrays to avoid null/undefined dereferences.
    const safeTags = tags ?? [];
    const safeViews = views ?? [];
    if (safeViews.length === 0 && !viewsRoleAttribute) {
      return '';
    }

    let _views = safeViews;
    const viewsRoleAttributeTag = viewsRoleAttribute ? `view:\${roleAttribute/${viewsRoleAttribute}}` : undefined;

    // filter out the viewsRoleAttribute if it exists
    if (viewsRoleAttribute) {
      _views = _views.filter((v) => v !== viewsRoleAttribute);
    }

    const viewsPrefixed = _views.map((v) => `${VIEW_PREFIX}${v}`);

    const hasTags = safeTags.length > 0 || !!tagsRoleAttribute;

    const ret: string[] = [];

    if (viewsRoleAttributeTag) {
      ret.push(viewsRoleAttributeTag);
    }

    ret.push(...viewsPrefixed);

    if (hasTags) {
      // return with comma
      return `,${ret.join(',')}`;
    }

    // return with semicolon
    return `;${ret.join(',')}`;
  }

  function getName() {
    switch (node.type) {
      case 'acct':
      case 'ip-allowlist':
        return '';
      default:
        if (!!node.roleAttribute) {
          return `\${roleAttribute/${node.roleAttribute}}`;
        }
        return node.name;
    }
  }

  switch (node.type) {
    case 'acct':
    case 'ip-allowlist':
      return node.type;

    // root scoped
    case 'code-reference-repository':
    case 'application':
    case 'domain-verification':
    case 'proj':
    case 'integration':
    case 'member':
    case 'relay-proxy-config':
    case 'role':
    case 'pending-request':
    case 'service-token':
    case 'team':
    case 'template':
    case 'webhook':
      return `${node.type}/${getName()}${stringifyTags(node.tags, node.tagsRoleAttribute)}`;

    // project scoped
    case 'ai-dataset':
    case 'ai-model-config':
    case 'ai-tool':
    case 'prompt-snippet':
    case 'ai-evaluation':
    case 'env':
    case 'payload-filter':
    case 'layer':
    case 'metric':
    case 'metric-group':
    case 'goal':
    case 'release-pipeline':
    case 'release-policy':
    case 'context-kind':
    case 'view':
    case 'session':
    case 'error':
    case 'log':
    case 'trace':
    case 'vega':
    case 'alert':
    case 'observability-dashboard':
    case 'agent-graph':
    case 'agent-optimization':
    case 'observability-settings':
      return `${toString(node.project)}:${node.type}/${getName()}${stringifyTags(node.tags, node.tagsRoleAttribute)}`;

    // proj + environment scoped, with view-constraint support
    case 'flag':
    case 'feature':
    case 'aiconfig':
      return `${toString(node.environment)}:${node.type}/${getName()}${stringifyTags(node.tags, node.tagsRoleAttribute)}${stringifyViews(node.tags, node.views, node.tagsRoleAttribute, node.viewsRoleAttribute)}`;
    case 'destination':
    case 'user':
    case 'holdout':
    case 'experiment':
    case 'multi-armed-bandit':
    case 'metric-data-source':
    case 'assignment-data-source':
    case 'product-analytics-dashboard':
    case 'notification-subscription':
    case 'sdk-key':
    case 'environment-release-plan':
      return `${toString(node.environment)}:${node.type}/${getName()}${stringifyTags(node.tags, node.tagsRoleAttribute)}`;
    case 'segment':
      // segments have view support like flags
      return `${toString(node.environment)}:${node.type}/${getName()}${stringifyTags(node.tags, node.tagsRoleAttribute)}${stringifyViews(node.tags, node.views, node.tagsRoleAttribute, node.viewsRoleAttribute)}`;

    case 'token':
      return `${toString(node.member)}:${node.type}/${getName()}${stringifyTags(node.tags, node.tagsRoleAttribute)}`;

    // application scoped
    case 'version':
      return `${toString(node.application)}:${node.type}/${getName()}${stringifyTags(node.tags, node.tagsRoleAttribute)}`;
  }
}
